import { useState, useEffect, useCallback, useRef } from 'react';

interface VersionInfo {
  version: string;
  buildTime: string;
  hash: string;
}

interface UseVersionCheckOptions {
  checkInterval?: number;
  enabled?: boolean;
}

interface UseVersionCheckReturn {
  updateAvailable: boolean;
  currentVersion: string;
  newVersion: string | null;
  checkForUpdates: () => Promise<void>;
  reloadApp: () => void;
  dismissUpdate: () => void;
  isDismissed: boolean;
}

const CURRENT_VERSION = (process.env.REACT_APP_VERSION as string | undefined) ?? '0.0.0';
const DEFAULT_CHECK_INTERVAL = 5 * 60 * 1000;
const FETCH_TIMEOUT = 5000;
// Delay first check so it doesn't compete with critical API calls on load
const INITIAL_CHECK_DELAY = 30000;

const CHUNK_ERROR_PATTERNS = [
  'chunkloaderror',
  'loading chunk',
  'failed to fetch dynamically imported module',
  "unexpected token '<'",
];

function isNewerVersion(current: string, server: string): boolean {
  if (!current || !server || current === server) return false;

  const cur = current.split('.').map(Number);
  const srv = server.split('.').map(Number);

  for (let i = 0; i < Math.max(cur.length, srv.length); i++) {
    if ((srv[i] || 0) > (cur[i] || 0)) return true;
    if ((srv[i] || 0) < (cur[i] || 0)) return false;
  }
  return false;
}

async function fetchVersionInfo(): Promise<VersionInfo | null> {
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), FETCH_TIMEOUT);
  try {
    const response = await fetch(`/version.json?t=${Date.now()}`, {
      cache: 'no-store',
      signal: controller.signal,
      headers: { 'Cache-Control': 'no-cache', Pragma: 'no-cache' },
    });
    if (!response.ok) return null;
    return await response.json();
  } catch {
    return null;
  } finally {
    clearTimeout(timer);
  }
}

export const useVersionCheck = (options: UseVersionCheckOptions = {}): UseVersionCheckReturn => {
  const { checkInterval = DEFAULT_CHECK_INTERVAL, enabled = true } = options;

  const [updateAvailable, setUpdateAvailable] = useState(false);
  const [newVersion, setNewVersion] = useState<string | null>(null);
  const [isDismissed, setIsDismissed] = useState(false);
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const lastCheckedRef = useRef<number>(0);
  // Ref mirrors state so async callbacks always read the current value without stale closures
  const updateAvailableRef = useRef(false);

  const checkForUpdates = useCallback(async () => {
    const info = await fetchVersionInfo();
    lastCheckedRef.current = Date.now();

    if (info && isNewerVersion(CURRENT_VERSION, info.version)) {
      updateAvailableRef.current = true;
      setUpdateAvailable(true);
      setNewVersion(info.version);
    }
  }, []);

  const reloadApp = useCallback(() => {
    if ('caches' in window) {
      caches.keys().then((names) => names.forEach((n) => caches.delete(n)));
    }
    window.location.reload();
  }, []);

  const dismissUpdate = useCallback(() => setIsDismissed(true), []);

  useEffect(() => {
    if (!enabled) return;

    const initialTimer = setTimeout(checkForUpdates, INITIAL_CHECK_DELAY);
    intervalRef.current = setInterval(checkForUpdates, checkInterval);

    return () => {
      clearTimeout(initialTimer);
      if (intervalRef.current) clearInterval(intervalRef.current);
    };
  }, [enabled, checkInterval, checkForUpdates]);

  // Stop polling once an update is confirmed — no point continuing
  useEffect(() => {
    if (updateAvailable && intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
  }, [updateAvailable]);

  useEffect(() => {
    if (!enabled) return;

    const handleVisibility = () => {
      if (
        document.visibilityState === 'visible' &&
        Date.now() - lastCheckedRef.current >= checkInterval
      ) {
        checkForUpdates();
      }
    };

    document.addEventListener('visibilitychange', handleVisibility);
    return () => document.removeEventListener('visibilitychange', handleVisibility);
  }, [enabled, checkInterval, checkForUpdates]);

  useEffect(() => {
    const handleChunkError = (event: ErrorEvent) => {
      const msg = String(event.error || event.message).toLowerCase();
      if (!CHUNK_ERROR_PATTERNS.some((p) => msg.includes(p))) return;

      checkForUpdates().then(() => {
        if (!updateAvailableRef.current) reloadApp();
      });
    };

    window.addEventListener('error', handleChunkError);
    return () => window.removeEventListener('error', handleChunkError);
  }, [checkForUpdates, reloadApp]);

  return {
    updateAvailable,
    currentVersion: CURRENT_VERSION,
    newVersion,
    checkForUpdates,
    reloadApp,
    dismissUpdate,
    isDismissed,
  };
};

export default useVersionCheck;
